/**
 * Exportacion e importacion de respaldos contra la base de datos (Req. 3).
 *
 * En un iPhone los datos locales pueden desaparecer por varias vias: borrar los
 * datos de navegacion, quedarse sin espacio, perder el telefono. Sin nube, el
 * archivo de respaldo es la UNICA red de seguridad, y por eso esta desde la
 * primera fase y no al final.
 *
 * La validacion del formato vive en backup-format.ts, que es codigo puro y
 * tiene pruebas. Aqui solo esta lo que lee y escribe en IndexedDB.
 */

import { db, contarRegistros, VERSION_ESQUEMA_ACTUAL, settingsIniciales } from './schema';
import { calcularChecksum } from './checksum';
import { enEscritura } from './tx';
import { ahoraIso } from '@/domain/dates';
import { TABLAS, escribirTabla, type MapaTablas } from './types';
import {
  IDENTIFICADOR_APP,
  FORMATO_VERSION,
  calcularRangoFechas,
  migrarDatos,
  tablasVacias,
  type ArchivoRespaldo,
} from './backup-format';

export * from './backup-format';

function modoDeEjecucion(): string {
  if (typeof window === 'undefined') return 'desconocido';
  const enStandalone =
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return enStandalone ? 'standalone' : 'navegador';
}

// ---------------------------------------------------------------------------
// Exportacion
// ---------------------------------------------------------------------------

/**
 * Lee todas las tablas de `TABLAS` y arma el archivo de respaldo (Req. 3.1, 3.2).
 * La lectura ocurre dentro de una transaccion para obtener una foto consistente.
 */
export async function exportarRespaldo(): Promise<ArchivoRespaldo> {
  const data = tablasVacias();

  await db.transaction('r', db.tables, async () => {
    for (const tabla of TABLAS) {
      const registros = await db.table(tabla).toArray();
      escribirTabla(data, tabla, registros);
    }
  });

  const counts = await contarRegistros();
  const checksum = await calcularChecksum(data);

  return {
    app: IDENTIFICADOR_APP,
    formatVersion: FORMATO_VERSION,
    schemaVersion: VERSION_ESQUEMA_ACTUAL,
    exportedAt: ahoraIso(),
    device: modoDeEjecucion(),
    counts,
    dateRange: calcularRangoFechas(data),
    checksum,
    data,
  };
}

// ---------------------------------------------------------------------------
// Importacion
// ---------------------------------------------------------------------------

/**
 * Reemplaza la totalidad de los datos con los del respaldo (Req. 3.9).
 *
 * Todo ocurre en UNA transaccion: si algo falla a medio camino, la base queda
 * exactamente como estaba.
 *
 * Quien llama a esta funcion debe haber generado antes el respaldo automatico
 * de seguridad que exige el Req. 3.8. La pantalla de respaldos lo hace.
 */
export async function importarRespaldo(archivo: ArchivoRespaldo): Promise<void> {
  const datos: MapaTablas =
    archivo.schemaVersion < VERSION_ESQUEMA_ACTUAL
      ? migrarDatos(archivo.data, archivo.schemaVersion)
      : archivo.data;

  await enEscritura(db.tables, async () => {
    for (const tabla of TABLAS) {
      await db.table(tabla).clear();
    }

    for (const tabla of TABLAS) {
      /**
       * Una tabla ausente se trata como vacia, no como error.
       *
       * Es el caso legitimo de un respaldo hecho con un esquema anterior: uno de
       * la version 2 no trae `cashMovements` porque esa tabla no existia. Quien
       * valida el archivo es `analizarRespaldo`, que ya avisa cuando falta una
       * tabla y bloquea la importacion si el archivo esta corrupto. Para cuando
       * se llega aqui, ausente significa vacia de verdad.
       *
       * Sin este respaldo, la importacion moria con "Cannot read properties of
       * undefined", que no le dice nada a quien lo ve en el telefono.
       */
      const registros = datos[tabla] ?? [];
      if (registros.length === 0) continue;
      await db.table(tabla).bulkPut(registros);
    }

    // La configuracion queda con la version de esquema vigente. El dato de
    // almacenamiento persistente se vuelve a detectar porque pertenece al
    // dispositivo, no al respaldo.
    const settings = await db.settings.get(1);
    if (settings === undefined) {
      await db.settings.put(settingsIniciales());
    } else {
      await db.settings.update(1, {
        schemaVersion: VERSION_ESQUEMA_ACTUAL,
        almacenamientoPersistente: null,
      });
    }
  });
}

/** Borra los datos de negocio y conserva la configuracion. */
export async function borrarTodosLosDatos(): Promise<void> {
  await enEscritura(db.tables, async () => {
    for (const tabla of TABLAS) {
      if (tabla === 'settings') continue;
      await db.table(tabla).clear();
    }
  });
}
